import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { Link } from 'react-router-dom';
import { useTranslation } from '@solara/i18n';
import { taxFilingApi, ApiError } from '@solara/api-client';
import type { TaxFilingDto } from '@solara/api-client';

export default function TaxFilingDetailPage() {
  const { t } = useTranslation('p1');
  const { filingId } = useParams<{ filingId: string }>();
  const [filing, setFiling] = useState<TaxFilingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (filingId) void load(filingId);
  }, [filingId]);

  async function load(id: string) {
    try {
      setFiling(await taxFilingApi.get(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.loadFiling'));
    }
  }

  async function approve() {
    if (!filing) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await taxFilingApi.clientApprove(filing.id);
      setFiling(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.approveError'));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (!filing) return;
    if (rejectReason.length < 5) {
      setError(t('taxFiling.rejectReasonRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await taxFilingApi.clientReject(filing.id, rejectReason);
      setFiling(updated);
      setRejectReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.rejectError'));
    } finally {
      setBusy(false);
    }
  }

  if (!filing) {
    return (
      <div className="p-6 text-sm text-fg-muted">
        {error ?? t('taxFiling.loading')}
      </div>
    );
  }

  const aed = (minor: number) => (minor / 100).toLocaleString();
  const canDecide = filing.status === 'AWAITING_CLIENT_APPROVAL';

  return (
    <div className="space-y-6 max-w-4xl">
      <header className="flex items-center justify-between">
        <div>
          <Link to="/tax/filings" className="text-xs text-fg-muted hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3 rtl-mirror" /> {t('taxFiling.backToFilings')}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('taxFiling.returnHeader', { type: filing.filingType })} —{' '}
            {filing.periodStart.slice(0, 10)} → {filing.periodEnd.slice(0, 10)}
          </h1>
          <p className="text-fg-muted text-sm">
            {t('taxFiling.statusLine', {
              status: t(`taxFiling.statuses.${filing.status}`, { defaultValue: filing.status }),
              date: new Date(filing.dueDate).toLocaleDateString(),
            })}
          </p>
        </div>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('taxFiling.vatReturnBlocks')}</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Item label={t('taxFiling.outputVatStandard')} value={aed(filing.outputVatStandardMinor)} />
            <Item label={t('taxFiling.outputVatZero')} value={aed(filing.outputVatZeroMinor)} />
            <Item label={t('taxFiling.outputVatExempt')} value={aed(filing.outputVatExemptMinor)} />
            <Item label={t('taxFiling.reverseChargeOutput')} value={aed(filing.reverseChargeOutputMinor)} />
            <Item label={t('taxFiling.inputVatRecoverable')} value={aed(filing.inputVatRecoverableMinor)} />
            <Item label={t('taxFiling.inputVatNonRecoverable')} value={aed(filing.inputVatNonRecoverableMinor)} />
            <Item label={t('taxFiling.reverseChargeInput')} value={aed(filing.reverseChargeInputMinor)} />
            <Item label={t('taxFiling.adjustments')} value={aed(filing.adjustmentsMinor)} />
            <Item label={t('taxFiling.netVatPayable')} value={aed(filing.netPayableMinor)} highlight />
          </dl>
          {filing.adjustmentReason && (
            <p className="mt-4 rounded bg-surface-muted p-3 text-xs">
              <strong>{t('taxFiling.taxAdvisorNote')}</strong> {filing.adjustmentReason}
            </p>
          )}
        </CardBody>
      </Card>

      {canDecide && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('taxFiling.decision')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4 text-sm">
            <Button onClick={approve} disabled={busy}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {t('taxFiling.approveSubmit')}
            </Button>
            <div className="border-t border-border pt-4 space-y-2">
              <p>{t('taxFiling.requestChangesHint')}</p>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('taxFiling.rejectReasonPlaceholder')}
              />
              <Button variant="ghost" onClick={reject} disabled={busy}>
                <XCircle className="mr-2 h-4 w-4" />
                {t('taxFiling.requestChanges')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Item({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={highlight ? 'col-span-2 rounded bg-surface-muted px-3 py-2' : ''}>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="font-mono text-sm">{value} AED</dd>
    </div>
  );
}
